import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import Teachr from "../../../Objects/models/Teachr";
import Functions from "../../../Helpers/Functions";
import FormInput from "../../../Components/Ui/FormInput";
import Teachr_Repository from "../../../Repositories/Teachr_Repository";
import { AppDispatch } from "../../../Redux/store";
import { userTeachrSetAccountAction } from "../../../Redux/Actions/UserActions";
import ValidateBIC from "../../../Validation/Bic";
import { ProfileTeachr } from "../../../Objects/models/TeachrUpdateRCS";
import { BpRadio, validateTva } from "../../../Utils/Function";
import AlertInfo from "../../../Components/AlertInfo";
import { useTailwindBrand } from "../../../Components/shared";
import {
  ENV_VERIFIED_TRAINER,
  getActiveAppBrandConfig,
  getActiveBrandConfig,
  getActivePrestataireBrandConfig,
  getActivePrestationBrandConfig,
  isBrandActive,
} from "../../../config/environment.config";
import ValidateIbanAici from "../../../Validation/IbanAici";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../../Redux/Reducers/MessageReducers";
import Settings from "../../../Components/Settings";
import SettingsSection from "../../../Components/SettingsSection";

type radioType = { label: string, value: number | string };

const FacturationStep = (props: { teachr: Teachr }) => {
  const { classes, brandConfig } = useTailwindBrand()
  const navigate = useNavigate()
  const { teachr } = props
  const dispatch = useDispatch<AppDispatch>();
  const [form, setForm] = useState({
    iban: teachr?.iban ?? "",
    bic: teachr?.bic ?? "",
    tva: (teachr?.tauxTva === "8.50" ? 8 : parseInt(teachr?.tauxTva as string)) ?? 0,
    tvaNumber: teachr?.tvaNumber ?? "",
    isTva: teachr?.tauxTva === '0.00' || teachr?.tauxTva === '0.00000000' || !teachr?.tauxTva ? 0 : 1
  });
  const [isEditing, setIsEditing] = useState(!form.iban && !form.bic && !form.tvaNumber ? true : false);
  const [error, setError] = useState({ iban: false, bic: false, tva: false });
  const [load, setLoad] = useState(false);

  const resetFormFromTeachr = useCallback(() => {
    setForm({
      iban: teachr?.iban ?? "",
      bic: teachr?.bic ?? "",
      tva: (teachr?.tauxTva === "8.50" ? 8 : parseInt(teachr?.tauxTva as string)) ?? 0,
      tvaNumber: teachr?.tvaNumber ?? "",
      isTva: teachr?.tauxTva === "0.00" || teachr?.tauxTva === "0.00000000" || !teachr?.tauxTva ? 0 : 1,
    });
    setError({ iban: false, bic: false, tva: false });
  }, [teachr]);

  const handleSave = () => updateTeachr();
  const handleCancel = () => {
    resetFormFromTeachr();
    setIsEditing(false);
  };
  const radioTva: radioType[] = [
    { label: "0%", value: "0" },
    { label: isBrandActive(ENV_VERIFIED_TRAINER) ? '8.5%' : "10%", value: isBrandActive(ENV_VERIFIED_TRAINER) ? "8" : "10" },
    { label: "20%", value: "20" },
  ]
  const radioChoice: radioType[] = [
    { label: "Oui", value: 1 },
    { label: "Non", value: 0 },
  ]
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "isTva" || name === "tva" ? parseInt(value as string) : value,
    }));
  }, []);

  const validate = () => {
    let error = {
      iban: false,
      bic: false,
      tva: false
    }

    if (!ValidateIbanAici(form.iban).isValid) error.iban = true;
    if (!ValidateBIC(form.bic).isValid) error.bic = true;
    if (form.isTva === 1 && form.tva !== 0 && !validateTva(form.tva, form.tvaNumber).isValid) error.tva = true;

    setError(error)

    if (error.iban || error.bic || error.tva) {
      return false
    } else {
      return true
    }
  }

  const updateTeachr = async () => {
    if (!validate()) return;
    setLoad(true);
    const updatedTeachr: Teachr = { ...teachr };
    updatedTeachr.iban = Functions.removeEspacesIBAN(form.iban.toUpperCase());
    updatedTeachr.bic = Functions.removeEspacesIBAN(form.bic.toUpperCase());
    updatedTeachr.tvaNumber = form.isTva === 0 ? "" : form.tvaNumber;
    updatedTeachr.tauxTva =
      form.isTva === 0 ? form.isTva.toString() : (form.tva === 8 ? 8.5 : form.tva).toString();

    const body = new ProfileTeachr(
      updatedTeachr.RCS ?? "",
      updatedTeachr.description,
      updatedTeachr?.address?.id,
      updatedTeachr.iban ?? "",
      updatedTeachr?.businessType ?? "",
      updatedTeachr.bic ?? "",
      updatedTeachr.tauxTva ?? "0",
      updatedTeachr.tvaNumber ?? ""
    );
    if (!updatedTeachr.id) {
      setLoad(false);
      return;
    }
    try {
      const response = await Teachr_Repository.updateProfilTeachr(updatedTeachr.id, body);
      if (response) {
        updatedTeachr.iban = response.iban;
        updatedTeachr.bic = response.bic;
        updatedTeachr.tauxTva = response.tauxTva.toString();
        updatedTeachr.tvaNumber = response.tvaNumber;
        const serializedTeachr = JSON.parse(JSON.stringify(updatedTeachr));
        if (serializedTeachr) {
          dispatch(userTeachrSetAccountAction(serializedTeachr));
          dispatch({ type: "SET_TEACHR_INFO", payload: { teachr: serializedTeachr } });
        }
        dispatch({
          type: SHOW_SUCCESS,
          payload: "Vos informations de facturation ont été mises à jour avec succès.",
          title: "Modifications enregistrées",
        });
        setIsEditing(false);
      }
    } catch (err: unknown) {
      const error = err as { _description?: string };
      if (error._description === "Warning: Undefined array key \"address\"") {
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, { state: { step: 1 } });
        dispatch({
          type: SHOW_ERROR,
          payload: "Veuillez renseigner une adresse avant de modifier vos informations de facturation.",
          title: "Renseigner une adresse",
        });
      }
    } finally {
      setLoad(false);
    }
  };

  // Informations de facturation
  const paymentInfo = [
    {
      label: "IBAN",
      value: Functions.addEspacesIBAN(form.iban),
      name: "iban",
      placeholder: "Ex : FRXX XXXX XXXX XXXX XXXX XXXX XXX",
      icon: <AccountBalanceIcon className="w-5 h-5" />,
      type: "iban" as const,
      error: error.iban,
      errorMsg: ValidateIbanAici(form.iban).message
    },
    {
      label: "BIC",
      value: form.bic,
      name: "bic",
      placeholder: "Ex : BRIDJJDJ",
      icon: <CreditCardIcon className="w-5 h-5" />,
      type: "bic" as const,
      error: error.bic,
      errorMsg: ValidateBIC(form.bic).message
    }
  ];

  return (
    <Settings title="Informations générales de facturation 📑"
      icon={
        <BusinessIcon sx={{
          color: brandConfig.colors?.primary ?? "primary.main",
          fontSize: 28
        }}
        />}
    >
      <SettingsSection
        title="Informations de facturation"
        subtitle="Gérez vos informations bancaires et fiscales"
        isEditing={isEditing}
        onEditClick={() => setIsEditing(true)}
        onSave={handleSave}
        onCancel={handleCancel}
        loading={load}
      // icon={
      // <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, borderRadius: 1, bgcolor: "success.50", border: "1px solid", borderColor: "success.100" }}>
      //   <SecurityIcon sx={{ color: "success.main", fontSize: 20 }} />
      //   <Typography variant="caption" fontWeight="600" color="success.dark">Sécurisé</Typography>
      // </Box>
      // }
      >
        <Grid item xs={12}>
          <Alert severity="info" className={`${classes.fontFamily} mb-6`}>
            <AlertTitle className={`${classes.fontFamily} `}> Sécurité des données</AlertTitle>
            Votre IBAN est conservé de manière entièrement sécurisée. Il nous sert
            uniquement à verser sur votre compte l'argent que vous gagnez en
            donnant des {getActivePrestationBrandConfig("s")} avec {getActiveAppBrandConfig()}.
          </Alert>
        </Grid>
        <Grid item xs={12} sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: 3, mb: 3 }}>
          <Typography
            variant="h6"
            component="h2"
            fontWeight="bold"
            fontFamily={brandConfig.fontFamily}
            color={brandConfig.colors?.primary ?? "text.primary"}
            className='border-b mb-6'
          >
            Informations bancaires
          </Typography>
          <Grid container spacing={2}>
            {paymentInfo.map((info, index) => (
              <Grid key={index} item xs={12} sm={12} md={12} lg={6}>
                <FormInput
                  label={info.label}
                  type="text"
                  name={info.name}
                  value={info.value}
                  placeholder={info.placeholder}
                  error={info.error ? info.errorMsg : undefined}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  isEditing={isEditing}
                  className={`${!isEditing ? 'cursor-not-allowed' : ''}`}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        {/* Configuration TVA */}
        <Grid item xs={12} sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1, p: 3 }}>
          <Typography
            variant="h6"
            component="h3"
            fontWeight="bold"
            fontFamily={brandConfig.fontFamily}
            color={brandConfig.colors?.primary ?? "text.primary"}
            sx={{ mb: 6 }}
            className='border-b mb-6'
          >
            Configuration TVA
          </Typography>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 1,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                fontWeight="500"
                fontFamily={brandConfig.fontFamily}
                color="text.primary"
                sx={{ flex: 1 }}
              >
                Je facture la TVA conformément à l&apos;article 293 B du CGI ?
              </Typography>
              <FormControl component="fieldset" disabled={!isEditing}>
                <RadioGroup
                  row
                  name="isTva"
                  onChange={handleChange}
                  value={form.isTva}
                >
                  {radioChoice.map((item, i) => (
                    <FormControlLabel
                      key={i}
                      value={item.value}
                      control={<BpRadio sx={{ color: brandConfig.colors?.primary }} />}
                      label={
                        <Typography variant="body2" fontFamily={brandConfig.fontFamily}>
                          {item.label}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>

          {form.isTva === 1 && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1,
                bgcolor: `${brandConfig.colors?.primary ?? "#1976d2"}14`,
                border: "1px solid",
                borderColor: `${brandConfig.colors?.primary ?? "#1976d2"}40`,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <ReceiptIcon sx={{ color: brandConfig.colors?.primary ?? "primary.main", fontSize: 22 }} />
                <Typography variant="subtitle2" fontWeight="600" fontFamily={brandConfig.fontFamily} sx={{ color: brandConfig.colors?.primary }}>
                  Configuration du taux de TVA
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, gap: 2 }}>
                  <Typography variant="body2" fontWeight="500" fontFamily={brandConfig.fontFamily}>
                    Taux de TVA facturé :
                  </Typography>
                  <FormControl component="fieldset" disabled={!isEditing}>
                    <RadioGroup row name="tva" onChange={handleChange} value={form.tva}>
                      {radioTva.map((item, i) => (
                        <FormControlLabel
                          key={i}
                          value={item.value}
                          control={<BpRadio sx={{ color: brandConfig.colors?.primary }} />}
                          label={
                            <Typography variant="body2" fontFamily={brandConfig.fontFamily}>
                              {item.label}
                            </Typography>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>
                {form.tva !== 0 && (
                  <Box sx={{ mt: 1 }}>
                    <FormInput
                      label="Numéro de TVA intracommunautaire"
                      type="text"
                      name="tvaNumber"
                      value={form.tvaNumber}
                      placeholder="Ex : FR76XXXXXXXXXX"
                      error={error.tva ? validateTva(form.tva, form.tvaNumber).message : undefined}
                      onChange={handleChange}
                      readOnly={!isEditing}
                      isEditing={isEditing}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {form.isTva === 1 && (
            <Box sx={{ mt: 2 }}>
              <AlertInfo
                info={isBrandActive(ENV_VERIFIED_TRAINER) ? "success" : "info"}
                msg={
                  <Typography variant="body2" fontFamily={brandConfig.fontFamily} component="span">
                    Êtes-vous sûr de réellement facturer la TVA ? En tant que {getActivePrestataireBrandConfig()}, vous pourriez normalement bénéficier d&apos;une exonération sur la TVA selon certaines conditions. Vous pouvez contacter votre conseiller à l&apos;adresse <strong style={{ color: brandConfig.colors?.primary }}>{process.env.REACT_APP_MAIL_SUPPORT}</strong> pour en savoir plus.
                  </Typography>
                }
                customClass=""
              />
            </Box>
          )}
        </Grid>
      </SettingsSection>

    </Settings>)
};

export default FacturationStep;